export interface InsuranceType {
  id: string;
  name: string;
  icon: string;
  popular?: boolean;
  fields: InsuranceField[];
}

export interface InsuranceField {
  id: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'radio' | 'checkbox';
  options?: string[];
  required?: boolean;
  placeholder?: string;
}

export interface InsuranceCompany {
  id: string;
  name: string;
  description: string;
  features: string[];
}

export const insuranceCompanies: InsuranceCompany[] = [
  {
    id: 'tryg',
    name: 'Tryg',
    description: 'Danmarks største forsikringsselskab med over 100 års erfaring',
    features: ['24/7 kundeservice', 'Hurtig skadebehandling', 'Digital selvbetjening']
  },
  {
    id: 'topdanmark',
    name: 'Topdanmark',
    description: 'Specialister i skræddersyede forsikringsløsninger',
    features: ['Personlig rådgivning', 'Fleksible dækninger', 'Fordelagtige rabatter']
  },
  {
    id: 'alm_brand',
    name: 'Alm. Brand',
    description: 'Dansk forsikringsselskab med fokus på kvalitet og service',
    features: ['Omfattende dækninger', 'Konkurrencedygtige priser', 'Erfarne rådgivere']
  },
  {
    id: 'alka',
    name: 'Alka',
    description: 'Et af Danmarks mest populære forsikringsselskaber',
    features: ['Konkurrencedygtige priser', 'Nem digital selvbetjening', 'Høj kundetilfredshed']
  },
  {
    id: 'codan',
    name: 'Codan',
    description: 'Erfaren forsikringspartner med fokus på tryghed',
    features: ['Omfattende dækninger', 'Professionel rådgivning', 'Hurtig skadebehandling']
  }
];

export const insuranceTypes: InsuranceType[] = [
  {
    id: 'car',
    name: 'Bilforsikring',
    icon: 'Car',
    popular: true,
    fields: [
      {
        id: 'licensePlate',
        label: 'Indtast nummerplade',
        type: 'text',
        required: true,
        placeholder: 'AB 12 345'
      },
      {
        id: 'yearlyDriving',
        label: 'Årlig kørsel i km',
        type: 'number',
        required: true
      },
      {
        id: 'insuranceYears',
        label: 'Antal år med bilforsikring i eget navn',
        type: 'select',
        options: ['0', '1', '2', '3', '4', '5+'],
        required: true
      },
      {
        id: 'coverage',
        label: 'Ønsket dækning',
        type: 'radio',
        options: ['Kasko', 'Ansvar'],
        required: true
      },
      {
        id: 'deductible',
        label: 'Selvrisiko',
        type: 'select',
        options: ['2.500 kr', '5.000 kr', '7.500 kr'],
        required: true
      },
      {
        id: 'claims',
        label: 'Antal skader de seneste 3 år',
        type: 'select',
        options: ['0', '1', '2', '3+'],
        required: true
      },
      {
        id: 'roadside',
        label: 'Ønsker vejhjælp',
        type: 'checkbox'
      },
      {
        id: 'extraCoverage',
        label: 'Interesse i ekstra dækninger',
        type: 'checkbox'
      }
    ]
  },
  {
    id: 'house',
    name: 'Husforsikring',
    icon: 'Home',
    popular: true,
    fields: [
      {
        id: 'currentAddress',
        label: 'Er det din nuværende adresse?',
        type: 'radio',
        options: ['Ja', 'Nej'],
        required: true
      },
      {
        id: 'buildYear',
        label: 'Byggeår',
        type: 'select',
        options: Array.from({ length: 123 }, (_, i) => (2024 - i).toString()),
        required: true
      },
      {
        id: 'houseType',
        label: 'Boligtype',
        type: 'select',
        options: ['Villa', 'Rækkehus', 'Lejlighed', 'Andet'],
        required: true
      },
      {
        id: 'roofType',
        label: 'Tagtype',
        type: 'select',
        options: ['Tegl', 'Eternit', 'Pap', 'Andet'],
        required: true
      },
      {
        id: 'size',
        label: 'Boligareal (m²)',
        type: 'number',
        required: true
      },
      {
        id: 'basement',
        label: 'Har boligen kælder',
        type: 'checkbox'
      },
      {
        id: 'alarm',
        label: 'Har boligen tyverialarm',
        type: 'checkbox'
      }
    ]
  },
  {
    id: 'contents',
    name: 'Indboforsikring',
    icon: 'Package',
    popular: true,
    fields: [
      {
        id: 'sum',
        label: 'Forsikringssum',
        type: 'select',
        options: ['500.000 kr', '750.000 kr', '1.000.000 kr'],
        required: true
      },
      {
        id: 'deductible',
        label: 'Selvrisiko',
        type: 'select',
        options: ['2.500 kr', '5.000 kr', '7.500 kr'],
        required: true
      },
      {
        id: 'claims',
        label: 'Antal skader de seneste 3 år',
        type: 'select',
        options: ['0', '1', '2', '3+'],
        required: true
      },
      {
        id: 'extraCoverage',
        label: 'Ønsker ekstra dækning',
        type: 'checkbox'
      },
      {
        id: 'student',
        label: 'Er du studerende',
        type: 'checkbox'
      },
      {
        id: 'currentInsurance',
        label: 'Har du allerede indboforsikring',
        type: 'radio',
        options: ['Ja', 'Nej'],
        required: true
      }
    ]
  },
  {
    id: 'accident',
    name: 'Ulykkesforsikring',
    icon: 'Shield',
    popular: true,
    fields: [
      {
        id: 'adults',
        label: 'Antal voksne',
        type: 'select',
        options: ['1', '2'],
        required: true
      },
      {
        id: 'children',
        label: 'Antal børn',
        type: 'select',
        options: ['0', '1', '2', '3', '4+'],
        required: true
      },
      {
        id: 'coverage',
        label: 'Ønsket dækning',
        type: 'select',
        options: ['Basis', 'Udvidet'],
        required: true
      },
      {
        id: 'sum',
        label: 'Forsikringssum',
        type: 'select',
        options: ['500.000 kr', '1.000.000 kr', '1.500.000 kr'],
        required: true
      },
      {
        id: 'dental',
        label: 'Tandskadedækning',
        type: 'checkbox'
      },
      {
        id: 'death',
        label: 'Dødsdækning',
        type: 'checkbox'
      },
      {
        id: 'student',
        label: 'Er du studerende',
        type: 'checkbox'
      }
    ]
  },
  {
    id: 'travel',
    name: 'Rejseforsikring',
    icon: 'Plane',
    fields: [
      {
        id: 'type',
        label: 'Type rejseforsikring',
        type: 'select',
        options: ['Enkelt rejse', 'Årsrejse'],
        required: true
      },
      {
        id: 'coverage',
        label: 'Geografisk dækning',
        type: 'select',
        options: ['Europa', 'Verden'],
        required: true
      },
      {
        id: 'cancellation',
        label: 'Afbestillingsforsikring',
        type: 'checkbox'
      },
      {
        id: 'adults',
        label: 'Antal voksne',
        type: 'select',
        options: ['1', '2'],
        required: true
      },
      {
        id: 'children',
        label: 'Antal børn',
        type: 'select',
        options: ['0', '1', '2', '3', '4+'],
        required: true
      }
    ]
  },
  {
    id: 'motorcycle',
    name: 'MC-forsikring',
    icon: 'Bike',
    fields: [
      {
        id: 'licensePlate',
        label: 'Indtast nummerplade',
        type: 'text',
        required: true,
        placeholder: 'AB 12 345'
      },
      {
        id: 'insuranceYears',
        label: 'Antal år med bil/MC-forsikring',
        type: 'select',
        options: ['0', '1', '2', '3', '4', '5+'],
        required: true
      },
      {
        id: 'claims',
        label: 'Antal skader de seneste 3 år',
        type: 'select',
        options: ['0', '1', '2', '3+'],
        required: true
      },
      {
        id: 'coverage',
        label: 'Ønsket dækning',
        type: 'select',
        options: ['Ansvar', 'Kasko'],
        required: true
      },
      {
        id: 'deductible',
        label: 'Selvrisiko',
        type: 'select',
        options: ['2.500 kr', '5.000 kr', '7.500 kr'],
        required: true
      }
    ]
  },
  {
    id: 'boat',
    name: 'Bådforsikring',
    icon: 'Ship',
    fields: [
      {
        id: 'type',
        label: 'Bådtype',
        type: 'select',
        options: ['Motorbåd', 'Sejlbåd', 'Jolle', 'Andet'],
        required: true
      },
      {
        id: 'brand',
        label: 'Mærke og model',
        type: 'text',
        required: true
      },
      {
        id: 'buildYear',
        label: 'Byggeår',
        type: 'select',
        options: Array.from({ length: 50 }, (_, i) => (2024 - i).toString()),
        required: true
      },
      {
        id: 'length',
        label: 'Længde',
        type: 'number',
        required: true
      },
      {
        id: 'material',
        label: 'Byggemateriale',
        type: 'select',
        options: ['Glasfiber', 'Træ', 'Stål', 'Aluminium'],
        required: true
      },
      {
        id: 'value',
        label: 'Værdi inkl. ekstraudstyr',
        type: 'number',
        required: true
      },
      {
        id: 'motorType',
        label: 'Motortype',
        type: 'select',
        options: ['Indenbordsmotor', 'Påhængsmotor', 'Ingen motor'],
        required: true
      },
      {
        id: 'harbor',
        label: 'Liggeplads',
        type: 'text',
        required: true
      },
      {
        id: 'experience',
        label: 'Antal år med egen båd',
        type: 'select',
        options: ['0', '1-2', '3-5', '5+'],
        required: true
      },
      {
        id: 'certificate',
        label: 'Duelighedsbevis',
        type: 'checkbox'
      },
      {
        id: 'contents',
        label: 'Tilknyt indboforsikring for besparelse',
        type: 'checkbox'
      }
    ]
  },
  {
    id: 'ownership',
    name: 'Ejerskifteforsikring',
    icon: 'Building',
    fields: [
      {
        id: 'party',
        label: 'Hvilken part er du',
        type: 'select',
        options: ['Køber', 'Sælger'],
        required: true
      },
      {
        id: 'type',
        label: 'Boligtype',
        type: 'select',
        options: ['Villa', 'Rækkehus', 'Ejerlejlighed'],
        required: true
      },
      {
        id: 'address',
        label: 'Boligens adresse',
        type: 'text',
        required: true
      },
      {
        id: 'price',
        label: 'Handelspris',
        type: 'number',
        required: true
      },
      {
        id: 'size',
        label: 'Boligareal',
        type: 'number',
        required: true
      },
      {
        id: 'plotSize',
        label: 'Grundareal',
        type: 'number',
        required: true
      },
      {
        id: 'buildYear',
        label: 'Byggeår',
        type: 'select',
        options: Array.from({ length: 123 }, (_, i) => (2024 - i).toString()),
        required: true
      },
      {
        id: 'extendedCoverage',
        label: 'Ønsker udvidet ejerskifteforsikring',
        type: 'checkbox'
      },
      {
        id: 'houseCombined',
        label: 'Tilknyt husforsikring for samlet tilbud',
        type: 'checkbox'
      }
    ]
  },
  {
    id: 'trailer',
    name: 'Trailerforsikring',
    icon: 'Caravan',
    fields: [
      {
        id: 'brand',
        label: 'Mærke',
        type: 'text',
        required: true
      },
      {
        id: 'year',
        label: 'Årgang',
        type: 'select',
        options: Array.from({ length: 30 }, (_, i) => (2024 - i).toString()),
        required: true
      },
      {
        id: 'licensePlate',
        label: 'Nummerplade/stelnummer',
        type: 'text',
        required: true
      },
      {
        id: 'value',
        label: 'Værdi',
        type: 'number',
        required: true
      },
      {
        id: 'weight',
        label: 'Vægt',
        type: 'number',
        required: true
      },
      {
        id: 'insuranceType',
        label: 'Ønsket forsikringstype',
        type: 'select',
        options: ['Ansvar', 'Kasko'],
        required: true
      },
      {
        id: 'deductible',
        label: 'Selvrisiko',
        type: 'select',
        options: ['2.500 kr', '5.000 kr', '7.500 kr'],
        required: true
      },
      {
        id: 'carCombined',
        label: 'Tilknyt bilforsikring for besparelse',
        type: 'checkbox'
      }
    ]
  },
  {
    id: 'scooter',
    name: 'Scooter',
    icon: 'Bike',
    fields: [
      {
        id: 'vehicleType',
        label: 'Type køretøj',
        type: 'select',
        options: ['Scooter', 'Knallert 30', 'Knallert 45'],
        required: true
      },
      {
        id: 'brand',
        label: 'Mærke og model',
        type: 'text',
        required: true
      },
      {
        id: 'year',
        label: 'Årgang',
        type: 'select',
        options: Array.from({ length: 20 }, (_, i) => (2024 - i).toString()),
        required: true
      },
      {
        id: 'licensePlate',
        label: 'Nummerplade',
        type: 'text',
        required: true
      },
      {
        id: 'security',
        label: 'Godkendt lås og alarm',
        type: 'checkbox'
      },
      {
        id: 'driverAge',
        label: 'Alder på yngste fører',
        type: 'number',
        required: true
      },
      {
        id: 'coverage',
        label: 'Ønsket dækning',
        type: 'select',
        options: ['Ansvar', 'Kasko'],
        required: true
      },
      {
        id: 'accidentCombined',
        label: 'Tilknyt ulykkesforsikring for besparelse',
        type: 'checkbox'
      }
    ]
  }
];